/**
 * Lane A `/rpc` token — obtained from the NU GUI gateway's RFC 8693 broker
 * (#590, operator decision D11; contract: nugui-data#590 comment 6014169890,
 * gateway nugui-gateway#559/#569).
 *
 * Lane A accepts exactly ONE audience, `nudata-rpc-api` (nudata-api#822), and
 * only the gateway may exchange tokens. So the console never sends its own
 * token (aud = `nudata-client`) to Lane A. Instead:
 *
 *   POST {gateway origin}/api/nudata/rpc-token
 *     Authorization: Bearer <console access token>   (no body, credentials: "omit")
 *   200 { access_token, token_type: "Bearer", audience: "nudata-rpc-api",
 *         expires_in: 900, issued_token_type, scope }    no refresh token
 *
 * The exchanged token is kept in tokenStore's dedicated Lane A slot
 * (RPC_TOKEN_KEY, #714) — never the console slot — and reused until 60 s
 * before its `exp`, then exchanged again. Every failure yields `null`: Lane A
 * calls then go out unauthenticated and the Lane-A-owned screens render their
 * honest "unavailable" state. A broker refusal never signs the user out of
 * this console — the console API, not the broker, decides that session.
 */
import {
  AUTH_TOKEN_KEY,
  RPC_TOKEN_KEY,
  clearAuthValue,
  getAuthValue,
  setAuthValue,
} from "../tokenStore";
import { resolveGatewayUrl } from "./gatewayUrl";

export const RPC_TOKEN_BROKER_PATH = "/api/nudata/rpc-token";
export const LANE_A_AUDIENCE = "nudata-rpc-api";
const EXPIRY_SKEW_SECONDS = 60;

export type LaneATokenUnavailable =
  | "signed_out"
  | "broker_unconfigured"
  | "console_token_rejected"
  | "origin_not_allowed"
  | "broker_disabled"
  | "rate_limited"
  | "exchange_failed"
  | "unreachable"
  | "malformed_response";

let lastUnavailable: LaneATokenUnavailable | null = null;
let rateLimitedUntil = 0;
let inflight: Promise<string | null> | null = null;
/**
 * Console-session generation (#972). Bumped by `invalidateLaneAToken()` on
 * every sign-out / session clear, so an exchange that was in flight when the
 * session ended can never write its token back into the Lane A slot.
 */
let generation = 0;

function jwtExp(token: string): number | null {
  try {
    const part = token.split(".")[1] ?? "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

function stillValid(token: string | null): token is string {
  if (!token) return false;
  const exp = jwtExp(token);
  return exp !== null && exp - EXPIRY_SKEW_SECONDS > Date.now() / 1000;
}

/** Why the last attempt produced no token (for diagnostics/UI), or null. */
export function laneATokenUnavailableReason(): LaneATokenUnavailable | null {
  return lastUnavailable;
}

function unavailable(reason: LaneATokenUnavailable): null {
  lastUnavailable = reason;
  return null;
}

/** True while the session that started an exchange is still the live one. */
function sameSession(startedIn: number, consoleToken: string): boolean {
  return (
    startedIn === generation && getAuthValue(AUTH_TOKEN_KEY) === consoleToken
  );
}

async function exchange(): Promise<string | null> {
  const startedIn = generation;
  const consoleToken = getAuthValue(AUTH_TOKEN_KEY);
  if (!consoleToken) return unavailable("signed_out");
  if (Date.now() < rateLimitedUntil) return unavailable("rate_limited");

  const gateway = await resolveGatewayUrl();
  if (!gateway) return unavailable("broker_unconfigured");

  // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
  let res: Response;
  try {
    res = await fetch(`${gateway}${RPC_TOKEN_BROKER_PATH}`, {
      method: "POST",
      credentials: "omit",
      cache: "no-store",
      headers: { Authorization: `Bearer ${consoleToken}` },
    });
  } catch {
    return unavailable("unreachable");
  }

  if (res.status === 200) {
    let body: Record<string, unknown>;
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      return unavailable("malformed_response");
    }
    const token = body.access_token;
    // Signed out (or signed in again) while the broker was answering: the
    // token belongs to a session that no longer exists — drop it.
    if (!sameSession(startedIn, consoleToken)) return unavailable("signed_out");
    if (
      typeof token !== "string" ||
      String(body.token_type).toLowerCase() !== "bearer" ||
      body.audience !== LANE_A_AUDIENCE ||
      !stillValid(token)
    ) {
      return unavailable("malformed_response");
    }
    setAuthValue(RPC_TOKEN_KEY, token);
    lastUnavailable = null;
    return token;
  }

  if (res.status === 429) {
    const retryAfter = Number(res.headers.get("Retry-After"));
    rateLimitedUntil =
      Date.now() +
      (Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60) * 1000;
    return unavailable("rate_limited");
  }
  if (res.status === 401) return unavailable("console_token_rejected");
  if (res.status === 403) return unavailable("origin_not_allowed");
  if (res.status === 404) return unavailable("broker_disabled");
  return unavailable("exchange_failed");
}

/**
 * A Lane A bearer token, or null when none can be obtained.
 * `forceRefresh` discards the cached one first (e.g. after Lane A answered 401).
 */
export function getLaneAToken(
  options: { forceRefresh?: boolean } = {},
): Promise<string | null> {
  // No console session, no Lane A token — not even a still-valid cached one.
  if (!getAuthValue(AUTH_TOKEN_KEY)) {
    clearAuthValue(RPC_TOKEN_KEY);
    return Promise.resolve(unavailable("signed_out"));
  }
  if (options.forceRefresh) {
    clearAuthValue(RPC_TOKEN_KEY);
  } else {
    const cached = getAuthValue(RPC_TOKEN_KEY);
    if (stillValid(cached)) return Promise.resolve(cached);
  }
  if (!inflight) {
    const current: Promise<string | null> = exchange().finally(() => {
      if (inflight === current) inflight = null;
    });
    inflight = current;
  }
  return inflight;
}

/**
 * The console session ended (sign-out, expiry, rejected session): drop the
 * Lane A token and orphan any exchange still in flight (#972). Called by
 * ssoSession.clearSession().
 */
export function invalidateLaneAToken(): void {
  generation += 1;
  inflight = null;
  clearAuthValue(RPC_TOKEN_KEY);
}

/** Test seam. */
export function resetLaneATokenForTests(): void {
  lastUnavailable = null;
  rateLimitedUntil = 0;
  inflight = null;
}
